import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';

const LandingPage = lazy(() => import('./pages/LandingPage').then((module) => ({ default: module.LandingPage })));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage').then((module) => ({ default: module.AdminLoginPage })));
const PlatformLayout = lazy(() => import('./pages/PlatformLayout').then((module) => ({ default: module.PlatformLayout })));
const AssistantPage = lazy(() => import('./pages/AssistantPage').then((module) => ({ default: module.AssistantPage })));
const JobsPage = lazy(() => import('./pages/JobsPage').then((module) => ({ default: module.JobsPage })));
const AdminPage = lazy(() => import('./pages/AdminPage').then((module) => ({ default: module.AdminPage })));
const KnowledgePage = lazy(() => import('./pages/KnowledgePage').then((module) => ({ default: module.KnowledgePage })));
const VerificationPage = lazy(() => import('./pages/VerificationPage').then((module) => ({ default: module.VerificationPage })));
const PublicVerificationPage = lazy(() => import('./pages/PublicVerificationPage').then((module) => ({ default: module.PublicVerificationPage })));
const PlanProcessingPage = lazy(() => import('./pages/PlanProcessingPage').then((module) => ({ default: module.PlanProcessingPage })));
const ResultsPage = lazy(() => import('./pages/ResultsPage').then((module) => ({ default: module.ResultsPage })));
const GeneralStudioPage = lazy(() => import('./pages/GeneralStudioPage').then((module) => ({ default: module.GeneralStudioPage })));
const LocalArchitectureStudioPage = lazy(() => import('./pages/LocalArchitectureStudioPage').then((module) => ({ default: module.LocalArchitectureStudioPage })));
const DrawingsOutputPage = lazy(() => import('./pages/DrawingsOutputPage').then((module) => ({ default: module.DrawingsOutputPage })));
const SpecialtyDrawingsPage = lazy(() => import('./pages/SpecialtyDrawingsPage').then((module) => ({ default: module.SpecialtyDrawingsPage })));
const ApprovalsPage = lazy(() => import('./pages/ApprovalsPage').then((module) => ({ default: module.ApprovalsPage })));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage').then((module) => ({ default: module.NotificationsPage })));

function Protected({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <main className="grid min-h-screen place-items-center bg-sand text-ink">
        <div className="animate-pulse text-lg font-bold">جارٍ تحميل منصة Details AI…</div>
      </main>
    );
  }
  return user ? children : <Navigate to="/admin/login" replace />;
}

export function App() {
  return (
    <Suspense fallback={<main className="grid min-h-screen place-items-center bg-sand text-ink"><div className="animate-pulse text-sm font-black">جارٍ تحميل المساحة…</div></main>}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/verify/:token" element={<PublicVerificationPage />} />
        <Route
          path="/app"
          element={
            <Protected>
              <PlatformLayout />
            </Protected>
          }
        >
          <Route index element={<AssistantPage />} />
          <Route path="jobs" element={<JobsPage />} />
          <Route path="knowledge" element={<KnowledgePage />} />
          <Route path="verification" element={<VerificationPage />} />
          <Route path="results" element={<ResultsPage />} />
          <Route path="studio" element={<GeneralStudioPage />} />
          <Route path="architecture" element={<LocalArchitectureStudioPage />} />
          <Route path="drawings" element={<DrawingsOutputPage />} />
          <Route path="specialty" element={<SpecialtyDrawingsPage />} />
          <Route path="approvals" element={<ApprovalsPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="plans" element={<PlanProcessingPage />} />
          <Route path="admin" element={<AdminPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
